import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useBrand } from "../BrandProvider";
import { BrandWave } from "./BrandWave";

interface BrandedSplitLayoutProps {
  children: React.ReactNode;
}

// Layout split compartilhado, extraído do LoginBranded (HRZ-288): painel visual da marca à
// esquerda (idêntico entre telas) + coluna de ação à direita (wordmark + children). Cores
// vêm do tema; nenhum hex, nenhum literal de marca. Reusado por LoginBranded e pelas telas
// Branded de senha.
export const BrandedSplitLayout: React.FC<BrandedSplitLayoutProps> = ({ children }) => {
  const { brand } = useBrand();

  return (
    <Grid container sx={{ minHeight: "100vh" }}>
      {/* Painel split: imagem de fundo (opcional) + tom primary por cima + onda + marca */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: { xs: 6, md: 8 },
          minHeight: { xs: 150, md: "100vh" },
          ...(brand.assets.loginBackground
            ? {
                backgroundImage: `url(${brand.assets.loginBackground})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : {}),
        }}
      >
        {/* Tom da cor primary sobreposto à imagem (opacidade configurável). */}
        {brand.assets.loginBackground && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "primary.main",
              opacity: brand.login?.backgroundOverlayOpacity ?? 1,
            }}
          />
        )}
        <BrandWave />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2 }}>
          {brand.assets.logoIcon && (
            <Box
              component="img"
              src={brand.assets.logoIcon}
              alt=""
              sx={{ width: 48, height: 48 }}
            />
          )}
          <Typography
            sx={{ fontWeight: 600, letterSpacing: "0.18em", fontSize: "2rem" }}
          >
            {brand.portalName}
          </Typography>
        </Box>
        {/* Textos do painel só no desktop; no mobile o header fica só com logo + título. */}
        <Box sx={{ position: "relative", maxWidth: 440, display: { xs: "none", md: "block" } }}>
          {brand.login?.eyebrow && (
            <Typography
              sx={{
                color: "secondary.main",
                fontWeight: 600,
                fontSize: "1.4rem",
                letterSpacing: "0.14em",
                mb: 1.5,
              }}
            >
              {brand.login.eyebrow}
            </Typography>
          )}
          <Typography sx={{ fontSize: "3.3rem", fontWeight: 600, lineHeight: 1.2 }}>
            {brand.login?.headline ?? "Acesse o seu portal."}
          </Typography>
          {brand.login?.subtitle && (
            <Typography sx={{ mt: 2, fontSize: "1.5rem", lineHeight: 1.6, opacity: 0.85 }}>
              {brand.login.subtitle}
            </Typography>
          )}
        </Box>

        {brand.login?.copyright && (
          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              // Sobe o texto para não encostar na linha laranja da onda (legibilidade).
              mb: { md: 6 },
              fontSize: "1.2rem",
              opacity: 0.8,
              display: { xs: "none", md: "block" },
            }}
          >
            {brand.login.copyright}
          </Typography>
        )}
      </Grid>

      {/* Lado do formulário: wordmark + conteúdo da tela (children) */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 4, md: 6 } }}
      >
        <Box sx={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Marca do formulário: símbolo (peso forte) ao lado do wordmark, para o traço
              fino do logotipo não destoar do símbolo laranja. Fallback: só o wordmark. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, alignSelf: "flex-start" }}>
            {brand.assets.logoIcon && (
              <Box
                component="img"
                src={brand.assets.logoIcon}
                alt=""
                aria-hidden="true"
                sx={{ width: 44, height: 44 }}
              />
            )}
            <Box
              component="img"
              src={brand.assets.logo}
              alt={brand.portalName}
              sx={{ width: brand.assets.logoIcon ? 176 : 220, height: "auto" }}
            />
          </Box>
          {children}
        </Box>
      </Grid>
    </Grid>
  );
};
